import {
  Box,
  Button,
  Checkbox,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Tab,
  TabList,
  Tabs,
  Text,
  Textarea,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactSelect from "react-select";
import { RiSearch2Fill } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import Layout from "../../../components/dashboard/Layout";
import { adminSelectStyles } from "../../../components/Models/admin-modal-theme";
import { brandPalette } from "../../../constants/brandPalette";
import AddStudent from "./models/add-student";
import UserQRCode from "./models/user-qr-code";
import StudentReport from "./models/student-report";
import StudentRosterTable from "./studentRosterTable";
import {
  getStudentsNotAttendedExamsRequest,
  getStudentsNotAttendedLiveVideosRequest,
  getStudentsNotViewedVideosRequest,
  sendBulkCustomMessageRequest,
} from "../../../modules/admin-modules/reports/Actions";
import { getAllVideosRequest } from "../../../modules/admin-modules/videos/Actions";
import { getAllExamsRequest } from "../../../modules/admin-modules/exams/Actions";
import { getLiveVideosRequest } from "../../../modules/admin-modules/liveVideos/Actions";
import { matchesStudentCodeSearch } from "../../../utils/studentCodeSearch";

const C = {
  primary: brandPalette.primary,
  secondary: brandPalette.accentDark,
  border: brandPalette.stone,
  muted: "rgba(30, 27, 23, 0.6)",
};

const ASSIGNMENT_KINDS = [
  { id: "quiz", label: "Quiz", noun: "quiz", types: [0] },
  { id: "exam", label: "Exam", noun: "exam", types: [1, 6] },
  { id: "hw", label: "HW", noun: "homework", types: [2] },
];

const tabSx = {
  px: 4,
  py: 2,
  borderRadius: "full",
  fontWeight: "700",
  fontSize: "sm",
  borderWidth: "1px",
  borderColor: "var(--border-subtle)",
  bg: "var(--surface-muted)",
  color: "var(--text-muted)",
  _selected: {
    bg: brandPalette.primary,
    color: brandPalette.cream,
    borderColor: brandPalette.primary,
  },
  _hover: {
    borderColor: brandPalette.primary,
    color: "var(--text-primary)",
  },
};

const selectBase = adminSelectStyles({ menuZIndex: 1600 });
const selectStyles = {
  ...selectBase,
  menuPortal: (base) => ({ ...base, zIndex: 1600 }),
  control: (styles, state) => ({
    ...selectBase.control(styles, state),
    borderRadius: "14px",
    minHeight: "44px",
  }),
};

function asList(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  return [];
}

function buildFollowUpMessage(selectedOptions) {
  const names = (selectedOptions || []).map((opt) => opt.label).filter(Boolean);
  const listed = names.join(", ");
  return `Edutopia Follow Up system - Kindly be noted that the student has not attended ${listed}`;
}

const RESOURCE_TABS = [
  {
    id: "videos",
    label: "Recorded Online Sessions",
    subtitle: "Students who have not watched the selected videos.",
    placeholder: "Select recorded online sessions",
    fieldLabel: "Recorded online sessions",
    fetchRequest: getStudentsNotViewedVideosRequest,
    calledParentNote: "Called parent/student because of videos missed",
  },
  {
    id: "exams",
    label: "Assignments (Exams, Quizzes, Homework)",
    subtitle: "Students who have not attended the selected assignments.",
    placeholder: "Select assignments",
    fieldLabel: "Assignments",
    fetchRequest: getStudentsNotAttendedExamsRequest,
    calledParentNote: "Called parent/student because of assignments missed",
  },
  {
    id: "live",
    label: "Live Sessions",
    subtitle: "Students who have not joined the selected live sessions.",
    placeholder: "Select live sessions",
    fieldLabel: "Live sessions",
    fetchRequest: getStudentsNotAttendedLiveVideosRequest,
    calledParentNote: "Called parent/student because of live videos missed",
  },
];

const MissedActivityStudents = () => {
  const dispatch = useDispatch();
  const model = useSelector((state) => state.model.isOpen18);
  const isLoading = useSelector((state) => state.reports.isLoading);
  const students = useSelector((state) => state.reports.missedActivityStudents);
  const videos = useSelector((state) => state.videos.videos);
  const exams = useSelector((state) => state.exams.exams);
  const liveVideos = useSelector((state) => state.adminLiveVideos.liveVideos);

  const [resource, setResource] = useState("videos");
  const [selectedIds, setSelectedIds] = useState([]);
  const [assignmentKind, setAssignmentKind] = useState("quiz");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedData, setSelectedData] = useState({});
  const [isAdd, setAdd] = useState(false);
  const [pageNumber, setPageNumber] = useState(0);
  const [size, setPerPage] = useState(50);
  const [message, setMessage] = useState(buildFollowUpMessage([]));
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);

  const config = RESOURCE_TABS.find((tab) => tab.id === resource) ?? RESOURCE_TABS[0];
  const resourceIndex = RESOURCE_TABS.findIndex((tab) => tab.id === config.id);

  const currentKind =
    ASSIGNMENT_KINDS.find((kind) => kind.id === assignmentKind) ?? ASSIGNMENT_KINDS[0];
  const assignmentKindIndex = ASSIGNMENT_KINDS.findIndex((kind) => kind.id === currentKind.id);

  useEffect(() => {
    dispatch(getAllVideosRequest());
    dispatch(getAllExamsRequest());
    dispatch(getLiveVideosRequest());
  }, [dispatch]);

  const options = useMemo(() => {
    if (resource === "videos") {
      return asList(videos).map((item) => ({
        value: item.id,
        label: item.title || item.name || `Recorded Online Session #${item.id}`,
      }));
    }
    if (resource === "live") {
      return asList(liveVideos).map((item) => ({
        value: item.id,
        label: item.title || item.name || `Live Session #${item.id}`,
      }));
    }
    return asList(exams)
      .filter((exam) => currentKind.types.includes(Number(exam?.type)))
      .map((exam) => ({
        value: exam.id,
        label: exam.name || `Assignment #${exam.id}`,
      }));
  }, [resource, videos, exams, liveVideos, currentKind]);

  const selectedOptions = useMemo(
    () => options.filter((opt) => selectedIds.includes(opt.value)),
    [options, selectedIds]
  );

  const allSessionsSelected = options.length > 0 && options.every((opt) => selectedIds.includes(opt.value));

  useEffect(() => {
    setMessage(buildFollowUpMessage(selectedOptions));
  }, [selectedOptions]);

  useEffect(() => {
    setPageNumber(0);
    setSelectedStudentIds([]);
    if (!selectedIds.length) return;
    dispatch(config.fetchRequest(selectedIds));
  }, [selectedIds, config, dispatch]);

  useEffect(() => {
    setPageNumber(0);
  }, [searchTerm]);

  const filteredStudents = useMemo(() => {
    const list = Array.isArray(students) ? students : [];
    const term = searchTerm.trim().toLowerCase();
    return list.filter((row) => {
      const student = row?.student && typeof row.student === "object" ? { ...row.student, ...row } : row;
      if (matchesStudentCodeSearch(student?.user_name, searchTerm)) return true;
      if (!term) return true;
      const name = String(student?.name ?? "").toLowerCase();
      const phone = String(student?.phone ?? "");
      return name.includes(term) || phone.includes(term);
    });
  }, [students, searchTerm]);

  const handleResourceChange = (index) => {
    const next = RESOURCE_TABS[index];
    if (!next || next.id === resource) return;
    setResource(next.id);
    setSelectedIds([]);
    setSearchTerm("");
    setPageNumber(0);
    setAssignmentKind("quiz");
    setSelectedStudentIds([]);
    setMessage(buildFollowUpMessage([]));
  };

  const handleKindChange = (index) => {
    const next = ASSIGNMENT_KINDS[index];
    if (!next || next.id === assignmentKind) return;
    setAssignmentKind(next.id);
    setSelectedIds([]);
    setSelectedStudentIds([]);
  };

  const handleSelectAllSessions = () => {
    if (allSessionsSelected) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(options.map((opt) => opt.value));
  };

  const studentIds = useMemo(
    () =>
      filteredStudents
        .map((row) => {
          const student = row?.student && typeof row.student === "object" ? { ...row.student, ...row } : row;
          return student?.id;
        })
        .filter((id) => id != null),
    [filteredStudents]
  );

  const allStudentsSelected =
    studentIds.length > 0 &&
    studentIds.every((id) => selectedStudentIds.map(String).includes(String(id)));

  const handleToggleStudent = useCallback((studentId) => {
    if (studentId == null) return;
    setSelectedStudentIds((prev) =>
      prev.some((id) => String(id) === String(studentId))
        ? prev.filter((id) => String(id) !== String(studentId))
        : [...prev, studentId]
    );
  }, []);

  const handleSelectAllStudents = (checked) => {
    setSelectedStudentIds(checked ? studentIds : []);
  };

  const handleSendFollowUp = () => {
    const text = message.trim();
    if (!text || !selectedStudentIds.length) return;
    dispatch(
      sendBulkCustomMessageRequest({
        student_ids: selectedStudentIds,
        message: text,
        receipent_phone: "parent",
      })
    );
    setSelectedStudentIds([]);
  };

  return (
    <Layout>
      <Box px={{ base: 4, md: 8 }} pt={6} pb={0} w="100%">
        <Flex
          align={{ base: "flex-start", md: "center" }}
          justify="space-between"
          gap={6}
          flexDir={{ base: "column", md: "row" }}
          mb={6}
        >
          <Box>
            <Text
              fontSize={{ base: "32px", md: "54px" }}
              fontWeight="800"
              color={C.primary}
              fontFamily="'Amiri', 'Tajawal', sans-serif"
              lineHeight="1"
            >
              Missed activity
            </Text>
            <Text mt={3} color={C.muted} fontSize="sm" maxW="2xl">
              {config.subtitle}
            </Text>
            <Text mt={2} fontSize="sm" fontWeight="700" color={C.secondary}>
              Total matching: {selectedIds.length ? filteredStudents.length : 0}
            </Text>
          </Box>
        </Flex>

        <Box
          mb={6}
          p={{ base: 4, md: 5 }}
          bg="var(--surface-raised)"
          border="1px solid"
          borderColor={C.border}
          borderRadius="16px"
          boxShadow="sm"
        >
          <Tabs
            index={resourceIndex}
            onChange={handleResourceChange}
            variant="unstyled"
            mb={4}
          >
            <TabList flexWrap="wrap" gap={2}>
              {RESOURCE_TABS.map((tab) => (
                <Tab key={tab.id} {...tabSx}>
                  {tab.label}
                </Tab>
              ))}
            </TabList>
          </Tabs>

          {resource === "exams" ? (
            <Tabs
              index={assignmentKindIndex}
              onChange={handleKindChange}
              variant="unstyled"
              mb={4}
            >
              <TabList flexWrap="wrap" gap={2}>
                {ASSIGNMENT_KINDS.map((kind) => (
                  <Tab key={kind.id} {...tabSx}>
                    {kind.label}
                  </Tab>
                ))}
              </TabList>
            </Tabs>
          ) : null}

          <Flex align="center" justify="space-between" gap={3} mb={3} flexWrap="wrap">
            <Text fontSize="xs" fontWeight="800" color={C.primary} textTransform="uppercase" letterSpacing="0.12em">
              {resource === "exams" ? currentKind.label : config.fieldLabel}
            </Text>
            <Button
              size="sm"
              variant="outline"
              borderRadius="full"
              fontWeight="800"
              borderColor={C.border}
              color={C.primary}
              isDisabled={!options.length}
              onClick={handleSelectAllSessions}
            >
              {allSessionsSelected ? "Clear all" : "Select all"}
            </Button>
          </Flex>
          <Box maxW="720px">
            <ReactSelect
              isMulti
              options={options}
              value={selectedOptions}
              onChange={(opts) => setSelectedIds((opts || []).map((opt) => opt.value))}
              styles={selectStyles}
              placeholder={
                resource === "exams"
                  ? `Select ${currentKind.noun}s`
                  : config.placeholder
              }
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              menuPosition="fixed"
            />
          </Box>
          <Box mt={4} maxW="720px">
            <Text fontSize="xs" fontWeight="800" color={C.primary} textTransform="uppercase" letterSpacing="0.12em" mb={2}>
              Follow-up message
            </Text>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              borderRadius="14px"
              borderColor={C.border}
              bg="var(--surface-raised)"
              fontWeight="600"
              _focus={{ borderColor: C.primary, boxShadow: "0 0 0 1px rgba(91, 13, 22, 0.2)" }}
            />
          </Box>
        </Box>

        <Box mb={6} maxW="520px">
          <InputGroup>
            <InputLeftElement pointerEvents="none" h="full">
              <RiSearch2Fill color="rgba(91, 13, 22, 0.5)" fontSize="18px" />
            </InputLeftElement>
            <Input
              placeholder="Search by name, code, or mobile…"
              value={searchTerm}
              borderRadius="full"
              border="1px solid"
              borderColor={C.border}
              bg="var(--surface-raised)"
              pl={10}
              _focus={{ borderColor: C.primary, boxShadow: "0 0 0 1px rgba(91, 13, 22, 0.2)" }}
              onChange={(e) => setSearchTerm(e.target.value)}
              isDisabled={!selectedIds.length}
            />
          </InputGroup>
        </Box>
      </Box>

      {selectedIds.length ? (
        <>
          {filteredStudents.length > 0 ? (
            <Box px={{ base: 4, md: 8 }} mb={4}>
              <Flex
                justify="space-between"
                align={{ base: "stretch", md: "center" }}
                gap={3}
                flexWrap="wrap"
                p={{ base: 4, md: 5 }}
                bg="var(--surface-raised)"
                border="1px solid"
                borderColor={C.border}
                borderRadius="16px"
              >
                <Checkbox
                  isChecked={allStudentsSelected}
                  isIndeterminate={selectedStudentIds.length > 0 && !allStudentsSelected}
                  onChange={(e) => handleSelectAllStudents(e.target.checked)}
                  colorScheme="red"
                  fontWeight="700"
                  color="var(--text-primary)"
                >
                  Select all students
                  {selectedStudentIds.length > 0 ? ` (${selectedStudentIds.length})` : ""}
                </Checkbox>
                <Button
                  size="sm"
                  borderRadius="lg"
                  bg={C.primary}
                  color="white"
                  fontWeight="800"
                  isDisabled={!selectedStudentIds.length || !message.trim()}
                  isLoading={isLoading}
                  onClick={handleSendFollowUp}
                  _hover={{ bg: C.secondary }}
                >
                  Send follow-up
                </Button>
              </Flex>
            </Box>
          ) : null}
          <StudentRosterTable
            setAdd={setAdd}
            setSelectedData={setSelectedData}
            list={filteredStudents}
            isLoading={isLoading}
            count={filteredStudents.length}
            pageNumber={pageNumber}
            setPageNumber={setPageNumber}
            size={size}
            setPerPage={setPerPage}
            paginateClient
            compact
            calledParentNote={config.calledParentNote}
            selectable
            selectedIds={selectedStudentIds}
            onToggleStudent={handleToggleStudent}
          />
        </>
      ) : (
        <Box px={{ base: 4, md: 8 }} pb={10}>
          <Box
            p={8}
            bg="var(--surface-raised)"
            border="1px solid"
            borderColor={C.border}
            borderRadius="16px"
          >
            <Text fontWeight="600" color="var(--text-muted)">
              {resource === "exams"
                ? `Select one or more ${currentKind.noun}s to load students.`
                : `Select one or more ${config.fieldLabel.toLowerCase()} to load students.`}
            </Text>
          </Box>
        </Box>
      )}

      <AddStudent
        key={`missed-student-${isAdd}-${selectedData?.id ?? "new"}`}
        isAdd={isAdd}
        data={selectedData}
        setSelectedData={setSelectedData}
        setAddedStudent={() => {}}
      />
      <UserQRCode data={selectedData} />
      {model ? (
        <StudentReport student_id={selectedData?.id} selectedData={selectedData} />
      ) : null}
    </Layout>
  );
};

export default MissedActivityStudents;
